import Spinner from '@creator/ui/components/Spinner/Spinner';
import Text from '@creator/ui/components/Text/Text';
import Icon from '@src/basic-components/Icon/Icon';
import IconAndLabel from '@src/basic-components/IconAndLabel/IconAndLabel';
import MenuItem from '@src/basic-components/MenuItem/MenuItem';
import MenuPopover from '@src/basic-components/MenuPopover/MenuPopover';
import ModActivityModal from '@src/components/ModActivityModal/ModActivityModal';
import ReactionsList from '@src/components/Post/Reactions/ReactionsList/ReactionsList';
import ReportModal from '@src/components/ReportModal/ReportModal';
import { ModalContext } from '@src/context/modal-context';
import type { UseModal } from '@src/hooks/use-modal';
import { useStoreState } from '@src/model/hooks';
import { canDeletePostComment, canEditPostComment } from '@src/model/upvote/permission';
import { deleteComment } from '@src/store/comments/delete';
import { isCommentDeleted } from '@src/store/comments/helpers/comments';
import { useComment } from '@src/store/comments/hooks/useComment';
import { type FC, memo, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface CommentMenuPopoverProps {
    tokenName: string;
    commentId: string;
    onEditClick: () => void;
}

const CommentMenuPopover: FC<CommentMenuPopoverProps> = (props: CommentMenuPopoverProps) => {
    const { tokenName, commentId } = props;

    const { t } = useTranslation();
    const [isMenuVisible, setIsMenuVisible] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const { handleModal } = useContext<UseModal>(ModalContext);
    const isLoggedIn = useStoreState(state => state.user.isLoggedIn);
    const myUser = useStoreState(state => state.user.myUser);
    const comment = useComment(commentId);

    const isDeleted = isCommentDeleted(commentId);

    const canEdit = canEditPostComment(commentId);
    const canDelete = canDeletePostComment(commentId, tokenName);
    const isOwner = comment?.publisherId === myUser?.id;

    // Open modal to select hide comment reason, or unhide comment
    const handleHideCommentClick = () => {
        if (isOwner) return handleDeleteClick();
        if (isDeleted) return handleDeleteClick(); // Unhide without reason

        // Moderator hiding someone else's comment
        const options = [t('modActivities.hideComment.spam'), t('modActivities.hideComment.hateful'), t('modActivities.hideComment.nudity')];
        handleModal(
            <ModActivityModal
                type={'hideComment'}
                options={options}
                onDeleteComment={handleDeleteClick}
                subTitle={t('modActivities.modalTitle', { action: t('hide'), entity: t('comment.entity') })}
            />,
            { title: t('modActivities.actions.hideComment'), size: 'sm' }
        );
    };

    const handleDeleteClick = async (reason?: string, reasonText?: string) => {
        if (isDeleting) return; // Prevent double clicks

        setIsDeleting(true);

        try {
            await deleteComment({
                commentId,
                tokenName,
                hidden: !isDeleted,
                userId: myUser?.id,
                reason,
                reasonText,
            });
        } catch (error) {
            console.error('Delete failed:', error);
            alert(error.message);
        } finally {
            setIsMenuVisible(false);
            setIsDeleting(false);
        }
    };

    const handleEditClick = () => {
        const { onEditClick } = props;

        setIsMenuVisible(false);
        onEditClick();
    };

    const handleOpenReportClick = () => {
        setIsMenuVisible(false);
        const options = [t('reportOptions.content.commentIncludes'), t('reportOptions.content.harrasment')];
        handleModal(<ReportModal type="Comment" commentId={commentId} tokenName={tokenName} options={options} />, { title: t('report'), size: 'sm' });
    };

    const handleShowReactorsClick = () => {
        setIsMenuVisible(false);
        handleModal(<ReactionsList tokenName={tokenName} commentId={commentId} />, { size: 'sm' });
    };

    const onMenuPopoverTriggerClick = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        e.preventDefault();
        setIsMenuVisible(!isMenuVisible);
    };

    const renderTrigger = () => {
        return <IconAndLabel iconProps={{ className: 'text-lg' }} iconName="dotsVertical" onClick={onMenuPopoverTriggerClick} />;
    };

    const renderDeleteMenuItem = () => {
        if (!canDelete) return null;

        return (
            <MenuItem onClick={() => handleHideCommentClick()} isLoading={isDeleting}>
                <Icon name={isDeleted ? 'show' : 'bin'} className="mr-2 text-xl" />
                <Text className="font-normal cursor-pointer">{t(isDeleted ? 'unDelete' : 'delete')}</Text>
            </MenuItem>
        );
    };

    const renderEditMenuItem = () => {
        if (!canEdit || isDeleted) return null; // Can't edit deleted comment

        return (
            <MenuItem onClick={handleEditClick}>
                <Icon name="edit" className="mr-2 text-xl" />
                <Text className="font-normal cursor-pointer">{t('commentMenuPopover.edit')}</Text>
            </MenuItem>
        );
    };

    const renderReportMenuItem = () => {
        if (!isLoggedIn) return null;
        return (
            <MenuItem onClick={handleOpenReportClick}>
                <Icon name="error" className="mr-2 text-lg" />
                <Text>{t('report')}</Text>
            </MenuItem>
        );
    };

    const renderShowReactorsMenuItem = () => {
        return (
            <MenuItem onClick={handleShowReactorsClick}>
                <Icon name="thumbUp" className="mr-2 text-xl" />
                <Text>{t('showReactors')}</Text>
            </MenuItem>
        );
    };

    return (
        <MenuPopover renderTrigger={renderTrigger} onClickOutside={() => setIsMenuVisible(false)} popoverProps={{ isVisible: isMenuVisible }}>
            {/* Permission Actions */}
            {renderDeleteMenuItem()}
            {renderEditMenuItem()}
            {/* General Actions */}
            {/* {renderShowReactorsMenuItem()} */}
            {renderReportMenuItem()}
        </MenuPopover>
    );
};

export default memo(CommentMenuPopover);
